import React from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ChevronLeft,
  Flag,
  ListChecks,
  MountainSnow,
  Pencil,
  Plus,
  Route,
  Search,
  Trash2,
} from "lucide-react";
import {
  fetchUserCollection,
  updateUserCollection,
  deleteUserCollection,
  addUserCollectionItem,
  removeUserCollectionItem,
  searchFeatures,
  type ItemType,
  type SearchResultItem,
  type UserCollectionItem,
} from "../api/user-collections";
import { AtlasPanel } from "../components/AtlasPanel";
import { ProgressRing } from "../components/ProgressRing";
import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { Skel } from "../components/Skeleton";

const TYPE_ICON: Record<ItemType, React.ReactNode> = {
  peak: <MountainSnow size={18} />,
  landmark: <Flag size={18} />,
  trail: <Route size={18} />,
};

function itemMeta(item: UserCollectionItem): string {
  if (item.itemType === "peak") return item.elevationM != null ? `${item.elevationM}m` : "Peak";
  if (item.itemType === "landmark") return item.category ? item.category.replace(/_/g, " ") : "Landmark";
  if (item.totalSections != null && item.totalSections > 0) {
    return `${item.completedSections ?? 0}/${item.totalSections} sections`;
  }
  return item.distanceKm != null ? `${item.distanceKm.toFixed(1)} km` : "Trail";
}

function AddItemPanel({ collectionId }: { collectionId: string }) {
  const qc = useQueryClient();
  const [q, setQ] = React.useState("");
  const [debouncedQ, setDebouncedQ] = React.useState("");
  const [addedIds, setAddedIds] = React.useState<Set<string>>(new Set());

  React.useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isFetching } = useQuery({
    queryKey: ["user-collections/search", debouncedQ],
    queryFn: () => searchFeatures(debouncedQ),
    enabled: debouncedQ.length >= 2,
  });

  const mutation = useMutation({
    mutationFn: (item: SearchResultItem) =>
      addUserCollectionItem(collectionId, { itemType: item.itemType, itemId: item.id }),
    onSuccess: (_data, item) => {
      setAddedIds((prev) => new Set(prev).add(item.id));
      void qc.invalidateQueries({ queryKey: ["user-collections", collectionId] });
      void qc.invalidateQueries({ queryKey: ["user-collections"] });
    },
    onError: (err, item) => {
      if (err instanceof Error && /already/i.test(err.message)) {
        setAddedIds((prev) => new Set(prev).add(item.id));
      }
    },
  });

  const results = data?.items ?? [];

  return (
    <AtlasPanel title="Add an item" bodyStyle={{ padding: 0 }}>
      <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ position: "relative" }}>
          <Search
            size={15}
            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-faint)" }}
          />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search peaks, landmarks and trails…"
            style={{
              width: "100%",
              boxSizing: "border-box",
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              padding: "9px 12px 9px 34px",
              fontSize: 14,
              color: "var(--text-primary)",
              outline: "none",
            }}
          />
        </div>

        {debouncedQ.length >= 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {isFetching ? (
              <Skel height="36px" />
            ) : results.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--text-muted)", padding: "4px 2px" }}>
                No matches for &ldquo;{debouncedQ}&rdquo;
              </div>
            ) : (
              results.map((r) => {
                const added = addedIds.has(r.id);
                return (
                  <div
                    key={`${r.itemType}-${r.id}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 10,
                      padding: "8px 10px",
                      background: "var(--surface-sunken)",
                      borderRadius: "var(--radius-md)",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                      <span style={{ color: "var(--gold-400)", flex: "0 0 auto" }}>{TYPE_ICON[r.itemType]}</span>
                      <span
                        style={{
                          fontSize: 13.5,
                          color: "var(--text-primary)",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {r.name}
                      </span>
                    </div>
                    <Button
                      variant={added ? "secondary" : "primary"}
                      size="sm"
                      disabled={added || (mutation.isPending && mutation.variables?.id === r.id)}
                      leftIcon={<Plus size={13} />}
                      onClick={() => mutation.mutate(r)}
                    >
                      {added ? "Added" : "Add"}
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </AtlasPanel>
  );
}

function RenameDialog({
  collectionId,
  initialName,
  initialDescription,
  onClose,
}: {
  collectionId: string;
  initialName: string;
  initialDescription: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = React.useState(initialName);
  const [description, setDescription] = React.useState(initialDescription ?? "");

  const mutation = useMutation({
    mutationFn: () => updateUserCollection(collectionId, { name, description: description || null }),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["user-collections", collectionId] }),
        qc.invalidateQueries({ queryKey: ["user-collections"] }),
      ]);
      onClose();
    },
  });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.6)",
        backdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border-strong)",
          borderRadius: "var(--radius-xl)",
          padding: 28,
          maxWidth: 380,
          width: "90%",
          boxShadow: "var(--shadow-xl)",
        }}
      >
        <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", marginBottom: 16 }}>
          Edit list
        </h2>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              padding: "8px 12px",
              fontSize: 14,
              color: "var(--text-primary)",
              outline: "none",
              width: "100%",
              boxSizing: "border-box",
            }}
          />
        </label>
        <label style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 20 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)" }}>Description</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              padding: "8px 12px",
              fontSize: 14,
              color: "var(--text-primary)",
              outline: "none",
              width: "100%",
              boxSizing: "border-box",
            }}
          />
        </label>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!name.trim() || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function MyListDetailPage() {
  const params = useParams({ strict: false });
  const id = (params as { id?: string }).id ?? "";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [renameOpen, setRenameOpen] = React.useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["user-collections", id],
    queryFn: () => fetchUserCollection(id),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteUserCollection(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["user-collections"] });
      void navigate({ to: "/lists" });
    },
  });

  const removeItemMutation = useMutation({
    mutationFn: (item: UserCollectionItem) => removeUserCollectionItem(id, item.itemType, item.id),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["user-collections", id] }),
        qc.invalidateQueries({ queryKey: ["user-collections"] }),
      ]);
    },
  });

  if (isLoading || !data) {
    return (
      <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="160px" />
        <Skel height="200px" />
      </div>
    );
  }

  const { collection, items } = data;
  const itemCount = items.length;
  const completedCount = items.filter((i) => i.collected).length;
  const remaining = itemCount - completedCount;

  return (
    <div className="r-page" style={{ padding: 28, overflow: "auto", flex: 1, display: "flex", flexDirection: "column", gap: 20 }}>
      <button
        type="button"
        onClick={() => void navigate({ to: "/lists" })}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          alignSelf: "flex-start",
          background: "none",
          border: "none",
          color: "var(--text-secondary)",
          cursor: "pointer",
          padding: 0,
          fontFamily: "var(--font-sans)",
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        <ChevronLeft size={18} /> My lists
      </button>

      <div
        className="atlas-topo"
        style={{
          position: "relative",
          borderRadius: "var(--radius-xl)",
          border: "1px solid var(--border-default)",
          padding: 28,
          overflow: "hidden",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 28, flexWrap: "wrap" }}>
          <ProgressRing value={completedCount} max={Math.max(itemCount, 1)} size={120} stroke={12} label="Collected" />

          <div style={{ flex: 1, minWidth: 200 }}>
            <div className="eyebrow" style={{ marginBottom: 8 }}>Custom list</div>
            <h1
              style={{
                font: "var(--type-display)",
                fontSize: 38,
                color: "var(--text-primary)",
                margin: "0 0 8px",
                letterSpacing: "-.02em",
              }}
            >
              {collection.name}
            </h1>
            {collection.description && (
              <p style={{ fontSize: 14, color: "var(--text-secondary)", margin: "0 0 12px", maxWidth: 480 }}>
                {collection.description}
              </p>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Badge variant="gold" dot>{completedCount} / {itemCount}</Badge>
              {itemCount > 0 && remaining > 0 ? (
                <Badge variant="neutral">{remaining} to go</Badge>
              ) : itemCount > 0 ? (
                <Badge variant="success">Completed</Badge>
              ) : null}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, flexShrink: 0 }}>
            <Button variant="secondary" leftIcon={<Pencil size={15} />} onClick={() => setRenameOpen(true)}>
              Edit
            </Button>
            <Button
              variant="danger"
              leftIcon={<Trash2 size={15} />}
              onClick={() => {
                if (window.confirm(`Delete "${collection.name}"? This can't be undone.`)) {
                  deleteMutation.mutate();
                }
              }}
            >
              Delete list
            </Button>
          </div>
        </div>
      </div>

      <AddItemPanel collectionId={id} />

      <AtlasPanel title={`Items (${itemCount})`} bodyStyle={{ padding: 0 }}>
        {itemCount === 0 ? (
          <div style={{ padding: 16 }}>
            <EmptyState
              icon={<ListChecks size={18} />}
              title="This list is empty"
              hint="Search above to add peaks, landmarks or trails."
            />
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column" }}>
            {items.map((item) => (
              <div
                key={`${item.itemType}-${item.id}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  padding: "12px 18px",
                  borderBottom: "1px solid var(--border-subtle)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  <span
                    style={{
                      width: 36,
                      height: 36,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "var(--radius-md)",
                      background: item.collected ? "var(--accent-soft)" : "var(--surface-sunken)",
                      border: "1px solid var(--border-default)",
                      color: item.collected ? "var(--gold-400)" : "var(--text-faint)",
                    }}
                  >
                    {TYPE_ICON[item.itemType]}
                  </span>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{item.name}</div>
                    <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{itemMeta(item)}</div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "0 0 auto" }}>
                  {item.collected ? (
                    <Badge variant="gold" dot>Collected</Badge>
                  ) : (
                    <Badge variant="neutral">Not yet</Badge>
                  )}
                  <button
                    type="button"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => {
                      if (window.confirm(`Remove "${item.name}" from this list?`)) {
                        removeItemMutation.mutate(item);
                      }
                    }}
                    style={{
                      width: 30,
                      height: 30,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: "var(--radius-md)",
                      background: "transparent",
                      border: "1px solid var(--border-default)",
                      color: "var(--text-muted)",
                      cursor: "pointer",
                    }}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </AtlasPanel>

      {renameOpen && (
        <RenameDialog
          collectionId={id}
          initialName={collection.name}
          initialDescription={collection.description}
          onClose={() => setRenameOpen(false)}
        />
      )}
    </div>
  );
}
